import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { tap, catchError } from 'rxjs/operators';
import produce from 'immer';
import {
  AddressOrders,
  AddressType,
  buildAddressOrdersRequest,
  buildPatientAddressesRequest,
  mapAddressOrders,
  mapPatientAddresses,
  PatientAddress,
} from '../../models';
import { ApiService, EnvService, SentryService } from '../../services';
import {
  ApiStoreData,
  defaultApiStoreData,
  defaultApiStoreIterableData,
  InvalidApiResponseDataError,
  MissingApiRequestDataError,
} from '../utils';
import { Observable, of } from 'rxjs';
import { without } from 'lodash-es';
import { SetOrderAddresses } from '../order/orders.actions';
import { OrdersState } from '../order/orders.state';
import {
  FetchPatientAddresses,
  CreatePatientAddress,
  FetchPreviousShippingAddressOrdersIfNeeded,
  AddPatientAddressType,
  UpdateOrdersToDefaultShipping,
  ClearPreviousShippingAddressOrders,
} from './addresses.actions';
import { PatientState } from '../patient/patient.state';

export interface AddressesStateModel {
  addresses: ApiStoreData<PatientAddress[]>;
  createAddress: ApiStoreData<PatientAddress | null>;
  addPatientAddressType: ApiStoreData<null>;
  addressOrders: ApiStoreData<AddressOrders | null>;
  updateDefaultShippingOrders: ApiStoreData<null>;
}

@State<AddressesStateModel>({
  name: 'patientAddresses',
  defaults: {
    addresses: defaultApiStoreIterableData,
    createAddress: defaultApiStoreData,
    addPatientAddressType: defaultApiStoreData,
    addressOrders: defaultApiStoreData,
    updateDefaultShippingOrders: defaultApiStoreData,
  },
})
@Injectable({ providedIn: 'root' })
export class AddressesState {
  constructor(
    private apiService: ApiService,
    private store: Store,
    private sentry: SentryService,
    private envService: EnvService
  ) {}

  handleError = this.sentry.handleError;
  handleSuccess = this.sentry.handleSuccess;

  @Selector()
  static patientAddresses(state: AddressesStateModel): PatientAddress[] {
    return state.addresses.data;
  }

  @Selector()
  static patientAddressesState(state: AddressesStateModel): ApiStoreData<PatientAddress[]> {
    return state.addresses;
  }

  @Selector()
  static createAddressState(state: AddressesStateModel): ApiStoreData<PatientAddress | null> {
    return state.createAddress;
  }

  @Selector()
  static addPatientAddressTypeState(state: AddressesStateModel): ApiStoreData<null> {
    return state.addPatientAddressType;
  }

  @Selector()
  static addressOrdersState(state: AddressesStateModel): ApiStoreData<AddressOrders | null> {
    return state.addressOrders;
  }

  @Selector()
  static updateDefaultShippingOrdersState(state: AddressesStateModel): ApiStoreData<null> {
    return state.updateDefaultShippingOrders;
  }

  @Selector()
  static defaultShippingAddress(state: AddressesStateModel): PatientAddress | undefined {
    return state.addresses.data.find((a) => a.types.includes(AddressType.Shipping));
  }

  @Action(FetchPatientAddresses)
  fetchAddresses(ctx: StateContext<AddressesStateModel>) {
    ctx.setState(
      produce((draft) => {
        draft.addresses.isLoading = true;
      })
    );

    const patientId = this.store.selectSnapshot(PatientState.selectedPatientId);

    if (!patientId) {
      const error = new MissingApiRequestDataError(['Missing patient id']);
      return this.handleError({ ctx, error, stateKey: 'addresses' });
    }

    return this.apiService.getAddresses(patientId).pipe(
      tap({
        next: (response) => {
          const data = mapPatientAddresses(response.data[0]);

          if (data.length === 0) {
            const error = new InvalidApiResponseDataError(['Patient has no addresses']);
            this.handleError({ ctx, error, stateKey: 'addresses' });
          } else {
            this.handleSuccess({ ctx, data, stateKey: 'addresses' });
          }
        },
      }),
      catchError((error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'addresses' }))
    );
  }

  @Action(CreatePatientAddress)
  createAddress(ctx: StateContext<AddressesStateModel>, action: CreatePatientAddress) {
    ctx.setState(
      produce((draft) => {
        draft.createAddress.isLoading = true;
      })
    );

    const patientId = this.store.selectSnapshot(PatientState.selectedPatientId);
    const patientAddresses = ctx.getState().addresses.data;
    const previousDefaultShippingAddress = AddressesState.defaultShippingAddress(ctx.getState());

    if (!patientId) {
      const error = new MissingApiRequestDataError(['Missing patient id']);
      return this.handleError({ ctx, error, stateKey: 'createAddress' });
    }

    return this.apiService
      .updatePatientAddresses(patientId, buildPatientAddressesRequest(patientId, patientAddresses, action.formValues))
      .pipe(
        tap({
          next: (response) => {
            const updatedAddresses = mapPatientAddresses(response.data[0]);

            if (updatedAddresses.length === 0) {
              const error = new InvalidApiResponseDataError(['Patient has no addresses']);
              this.handleError({ ctx, error, stateKey: 'createAddress' });
            } else {
              const hasDefaultShippingAddressChanged =
                action.formValues.types.includes(AddressType.Shipping) && !!previousDefaultShippingAddress;

              const updateStateFunction = (draft: AddressesStateModel) => {
                draft.addresses.data = updatedAddresses;

                const newAddress = updatedAddresses.find(
                  (addr) => !patientAddresses.map((pa) => pa.id).includes(addr.id)
                )!;

                draft.createAddress.data = newAddress;
              };

              this.handleSuccess({ ctx, stateKey: 'createAddress', updateStateFunction });

              if (hasDefaultShippingAddressChanged) {
                this.store.dispatch(new FetchPreviousShippingAddressOrdersIfNeeded(previousDefaultShippingAddress.id));
              }
            }
          },
        }),
        catchError((error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'createAddress' }))
      );
  }

  @Action(AddPatientAddressType)
  addPatientAddressType(ctx: StateContext<AddressesStateModel>, action: AddPatientAddressType) {
    const patientId = this.store.selectSnapshot(PatientState.selectedPatientId);
    const patientAddresses = ctx.getState().addresses.data;
    const previousDefaultShippingAddress = AddressesState.defaultShippingAddress(ctx.getState());

    if (!patientId) {
      const error = new MissingApiRequestDataError(['Missing patient id']);
      return this.handleError({ ctx, error, stateKey: 'addPatientAddressType' });
    }

    // We will perform this update optimistically
    ctx.setState(
      produce((draft) => {
        draft.addPatientAddressType.isLoading = true;
        draft.addresses.data = draft.addresses.data.map((a) => ({ ...a, types: without(a.types, action.addressType) }));
        draft.addresses.data.find((a) => a.id === action.addressId)?.types?.push(action.addressType);
      })
    );

    const updatedPatientAddresses = ctx.getState().addresses.data;

    return this.apiService
      .updatePatientAddresses(patientId, buildPatientAddressesRequest(patientId, updatedPatientAddresses))
      .pipe(
        tap({
          next: () => {
            const hasDefaultShippingAddressChanged =
              action.addressType === AddressType.Shipping && !!previousDefaultShippingAddress;

            this.handleSuccess({ ctx, stateKey: 'addPatientAddressType' });

            if (hasDefaultShippingAddressChanged) {
              this.store.dispatch(new FetchPreviousShippingAddressOrdersIfNeeded(previousDefaultShippingAddress.id));
            }
          },
        }),
        catchError((error: Error): Observable<any> => {
          const updateStateFunction = (draft: AddressesStateModel) => {
            draft.addresses.data = patientAddresses;
          };

          return this.handleError({ ctx, error, stateKey: 'addPatientAddressType', updateStateFunction });
        })
      );
  }

  @Action(FetchPreviousShippingAddressOrdersIfNeeded)
  fetchPreviousShippingAddressOrdersIfNeeded(
    ctx: StateContext<AddressesStateModel>,
    action: FetchPreviousShippingAddressOrdersIfNeeded
  ) {
    if (!this.envService.features['addressOrdersChangeModal']?.enabled) {
      return of(null);
    }

    const currentDefaultShippingAddress = AddressesState.defaultShippingAddress(ctx.getState());
    const hasDefaultShippingAddressChanged =
      !!currentDefaultShippingAddress && currentDefaultShippingAddress.id !== action.addressId;

    ctx.setState(
      produce((draft) => {
        draft.addressOrders.isLoading = hasDefaultShippingAddressChanged;
      })
    );

    if (!hasDefaultShippingAddressChanged) {
      return of(null);
    }

    const patientId = this.store.selectSnapshot(PatientState.selectedPatientId);
    const allOrders = this.store.selectSnapshot(OrdersState.allOrders);

    if (!patientId) {
      const error = new MissingApiRequestDataError(['Missing patient id']);
      return this.handleError({ ctx, error, stateKey: 'addressOrders' });
    }

    return this.apiService.getAddressOrders(patientId, action.addressId).pipe(
      tap({
        next: (response) => {
          const data = mapAddressOrders(response.data, allOrders);
          this.handleSuccess({ ctx, data, stateKey: 'addressOrders' });
        },
      }),
      catchError((error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'addressOrders' }))
    );
  }

  @Action(UpdateOrdersToDefaultShipping)
  updateOrdersToDefaultShipping(ctx: StateContext<AddressesStateModel>, action: UpdateOrdersToDefaultShipping) {
    ctx.setState(
      produce((draft) => {
        draft.updateDefaultShippingOrders.isLoading = true;
      })
    );

    const patientId = this.store.selectSnapshot(PatientState.selectedPatientId);
    const allOrders = this.store.selectSnapshot(OrdersState.allOrders);
    const currentDefaultShippingAddress = ctx
      .getState()
      .addresses.data.find((a) => a.types.includes(AddressType.Shipping));

    if (!patientId) {
      const error = new MissingApiRequestDataError(['Missing patient id']);
      return this.handleError({ ctx, error, stateKey: 'updateDefaultShippingOrders' });
    }

    if (!currentDefaultShippingAddress) {
      return this.handleError({
        ctx,
        error: new MissingApiRequestDataError(['Missing default shipping address']),
        stateKey: 'updateDefaultShippingOrders',
      });
    }

    return this.apiService
      .updateAddressOrders(
        patientId,
        buildAddressOrdersRequest(patientId, currentDefaultShippingAddress.id, allOrders, action.formValues)
      )
      .pipe(
        tap({
          next: (_) => {
            this.store.dispatch(new SetOrderAddresses(action.formValues, currentDefaultShippingAddress));
            this.handleSuccess({ ctx, stateKey: 'updateDefaultShippingOrders' });
          },
        }),
        catchError(
          (error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'updateDefaultShippingOrders' })
        )
      );
  }

  @Action(ClearPreviousShippingAddressOrders)
  clearPreviousShippingAddressOrders(ctx: StateContext<AddressesStateModel>) {
    ctx.setState(
      produce((draft) => {
        draft.addressOrders.data = null;
      })
    );
  }
}
